import styled from 'styled-components';

import Icon from '@ant-design/icons';

import { createVar, sizeUp, vars } from '~/theme';
import { headerVars } from '~organisms/Header';

const _menuBarIconSize = createVar('_designSidePanel-iconSize');
const _menuBarWidth = createVar('_designSidePanel-menuBarWidth');
const boxShadow = '-10rem 5rem 15rem -5rem rgba(0,0,0,0.5)';
const menuBarPadding = sizeUp('7rem', ['md', '10rem']);
const menuBarBtnPadding = '10rem';

type AsteriskContainerProps = {
  $isSelected?: boolean;
};

export const AsteriskContainer = styled.div<AsteriskContainerProps>(
  ({ theme }) => ({
    position: 'absolute',
    top: 0,
    right: 3,
    color: theme.defaultTheme.promotionColor,
  })
);

export const Container = styled.div({
  ...vars({
    [_menuBarIconSize]: sizeUp('25rem', ['md', '30rem']),
    [_menuBarWidth]: `calc((${menuBarBtnPadding} * 2) + ${_menuBarIconSize})`,
  }),

  display: 'flex',

  transition: 'transform 250ms ease-in',

  '&[aria-expanded="false"]': {
    transform: `translateX(calc(100% - ${_menuBarWidth}))`,
  },
});

export const MenuBarBackdrop = styled.div({
  minInlineSize: `calc(${_menuBarWidth} + (${menuBarPadding} * 2))`,
});

export const MenuBar = styled.nav(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: '12rem',

  position: 'absolute',
  insetBlockStart: 0,
  insetInlineStart: 0,
  zIndex: theme.overlayPosition.layerImportant,

  width: _menuBarWidth,
  height: 'fit-content',
  padding: menuBarPadding,
  borderEndStartRadius: '10rem',
  backgroundColor: theme.palette.white,
  boxShadow: [`2px 0 0 ${theme.palette.white}`, boxShadow].join(', '),
}));

export const MenuBarIcon = styled(Icon)({
  boxSizing: 'border-box',

  display: 'flex',
  justifyContent: 'center',

  width: _menuBarIconSize,
  height: _menuBarIconSize,
  borderRadius: '10rem',
});

export const BtnLabel = styled.span(({ theme }) => ({
  display: 'none',

  fontSize: theme.typography.size20,
  whiteSpace: 'nowrap',
}));

export const MenuBarButton = styled.button(({ theme }) => ({
  '&::after': {
    content: '""',

    position: 'absolute',
    inset: 0,
    zIndex: theme.overlayPosition.layerNegative,

    borderRadius: 'inherit',
    boxShadow: `0 3rem 6rem ${theme.palette.black}80`,
    opacity: 0,

    transition: 'inherit',
  },

  position: 'relative',

  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '10rem',

  padding: menuBarBtnPadding,
  border: `1px solid ${theme.palette.lighterGray}`,
  borderRadius: '10rem',

  backgroundColor: 'transparent',

  transition: [
    'background-color 250ms ease-out',
    'opacity 250ms ease-out',
    'border-radius 50ms linear',
  ].join(', '),

  cursor: 'pointer',

  '&[aria-selected="false"][data-is-visited="true"] svg path': {
    fill: theme.palette.darkLiver,
  },

  '&[aria-selected="false"][data-is-visited="true"] svg rect': {
    fill: theme.palette.darkLiver,
    stroke: theme.palette.white,
  },

  '&[aria-selected="true"][data-invert-color-selected="true"] svg g': {
    stroke: theme.palette.white,
    fill: theme.palette.white,
  },

  '&[aria-selected="false"][data-is-visited="true"][data-invert-color-not-selected="true"] svg g':
    {
      stroke: theme.palette.darkLiver,
      fill: theme.palette.darkLiver,
    },

  '&[aria-selected="true"]': {
    backgroundColor: theme.palette.darkLiver,
    color: theme.palette.white,
  },

  '&:not([disabled]):hover, &[disabled][aria-selected="true"]:hover': {
    paddingInlineStart: '14rem',
    borderRadius: '25rem',

    '&::after': {
      opacity: 1,
    },

    [`${BtnLabel}`]: {
      display: 'inline',
    },
  },

  '&[aria-selected="false"]:not([disabled]):hover': {
    backgroundColor: theme.palette.white,
  },

  '&[aria-selected="false"][disabled]': {
    opacity: '0.5',
    cursor: 'not-allowed',
  },
}));

export const PanelContent = styled.div({
  flexGrow: 1,

  height: `max(auto, 100vh - ${headerVars.headerHeight})`,
  backgroundColor: 'white',

  transition: 'opacity 250ms ease-in',

  boxShadow,

  overflowY: 'auto',

  [`${Container}[aria-expanded="false"] &`]: {
    opacity: 0,
  },
});
